/**
 * Primary section "Notizen" on tenant or owner unit contract editor (EHV-575).
 * Header shows a section index only when `.PageContent` already contains at least one
 * `.section-number-wrapper` (same numbering pattern as the host). Index = count of
 * `:scope > .PrimarySectionWrapper` + 1. Otherwise the header is title-only.
 * Loads and saves via Lux API `GET|POST|DELETE /api/impowerX/unit-contracts/notes`.
 * Accordion coordination matches Mietminderung / Mietanpassung.
 * @param {{ unitContractId: number | null }} ctx
 */
const injectUnitContractNotes = (ctx) => {
  const SECTION_ID = 'UnitContractNotes'
  const unitContractId = ctx?.unitContractId ?? null
  if (unitContractId == null) return

  const existing = document.getElementById(SECTION_ID)
  if (existing) {
    if (existing.dataset.unitContractId === String(unitContractId)) return
    existing.remove()
  }

  const tenantEditor = document.querySelector('.TenantContractEditor')
  const ownerEditor = document.querySelector('.OwnerContractEditor')
  const editor = tenantEditor || ownerEditor
  if (!editor) return

  const pageContent =
    editor.querySelector('.PageContent[data-testid="page-content-undefined"]') ||
    editor.querySelector('.PageContent')
  if (!pageContent) return

  const pageUsesSectionNumbers = !!pageContent.querySelector('.section-number-wrapper')
  const notesSectionNumber =
    pageContent.querySelectorAll(':scope > .PrimarySectionWrapper').length + 1

  const apiRoot = typeof baseApiUrl !== 'undefined' && baseApiUrl ? baseApiUrl : ''

  const formatUser = (user) => {
    if (!user) return 'Unbekannt'
    const name = `${user.firstName || ''} ${user.lastName || ''}`.trim()
    return name || 'Unbekannt'
  }

  const formatWhen = (value) => {
    try {
      const d = new Date(value)
      return Number.isNaN(d.getTime()) ? '' : d.toLocaleString()
    } catch {
      return ''
    }
  }

  const createNoteElement = (note) => {
    const createdByName = formatUser(note.createdByUser)
    const createdAtLabel = formatWhen(note.createdAt)
    const metaLines = [`Erstellt: ${createdAtLabel} · ${createdByName}`]
    if (note.lastEditedAt && note.lastEditedByUser) {
      metaLines.push(
        `Zuletzt bearbeitet: ${formatWhen(note.lastEditedAt)} · ${formatUser(note.lastEditedByUser)}`
      )
    }
    return luxCreateNoteListItem({
      id: note.id,
      metaLines,
      bodyText: note.note || '',
      className: 'note-item lux-uc-note-item',
      editBtnClass: 'lux-uc-edit-note-btn',
      deleteBtnClass: 'lux-uc-delete-note-btn',
    })
  }

  const root = document.createElement('div')
  root.className = 'PrimarySectionWrapper UnitContractNotes'
  root.id = SECTION_ID
  root.dataset.unitContractId = String(unitContractId)
  root.setAttribute('data-testid', 'lux-unit-contract-notes')

  const wrapperContent = document.createElement('div')
  wrapperContent.className = 'section-wrapper-content'

  const header = document.createElement('div')
  header.className = 'section-header'
  header.setAttribute('role', 'button')
  header.setAttribute('tabindex', '0')

  const titleWrapper = document.createElement('div')
  titleWrapper.className = 'section-title-wrapper'

  const titleEl = document.createElement('div')
  titleEl.className = 'section-title'
  titleEl.textContent = 'Notizen'

  if (pageUsesSectionNumbers) {
    const numberWrap = document.createElement('div')
    numberWrap.className = 'section-number-wrapper'
    const numberEl = document.createElement('div')
    numberEl.className = 'section-number'
    numberEl.textContent = String(notesSectionNumber)
    numberWrap.appendChild(numberEl)
    titleWrapper.appendChild(numberWrap)
  }
  titleWrapper.appendChild(titleEl)
  header.appendChild(titleWrapper)

  const sectionBody = document.createElement('div')
  sectionBody.className = 'section-content section-content-with-padding'
  sectionBody.style.display = 'none'

  sectionBody.innerHTML = `
    <div class="lux-uc-notes-inner">
      <div class="ant-row ant-form-item ant-form-item-with-help" style="row-gap: 0px;">
        <div class="ant-col ant-form-item-control">
          <div class="ant-form-item-control-input">
            <div class="ant-form-item-control-input-content">
              <div class="notes-input-section">
                <textarea
                  class="ant-input lux-uc-notes-textarea"
                  placeholder="Neue Notiz…"
                  style="min-height: 100px; resize: vertical;"
                ></textarea>
                <div class="notes-actions" style="display: flex; justify-content: flex-end; margin-top: 8px;">
                  <button type="button" class="ant-btn ant-btn-default Button lux-uc-add-note-btn">
                    <span>Erstellen</span>
                  </button>
                </div>
              </div>
              <div class="ant-divider" style="margin-top: 8px;"></div>
              <div class="lux-uc-notes-list" style="max-height: 400px; overflow-y: auto; margin-top: 24px;">
                <div class="lux-uc-notes-loading" style="text-align: center; padding: 24px 0; color: rgba(0,0,0,0.45);">
                  Notizen werden geladen…
                </div>
                <div class="lux-uc-notes-empty" style="display: none; text-align: center; padding: 24px 0; color: rgba(0, 0, 0, 0.45);">
                  Keine Notizen vorhanden.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `

  const textarea = sectionBody.querySelector('.lux-uc-notes-textarea')
  const addBtn = sectionBody.querySelector('.lux-uc-add-note-btn')
  const listEl = sectionBody.querySelector('.lux-uc-notes-list')
  const loadingEl = sectionBody.querySelector('.lux-uc-notes-loading')
  const emptyEl = sectionBody.querySelector('.lux-uc-notes-empty')
  let editsNoteId = null
  /** Last rendered notes from server; used to recover UI if reload fails after a successful save. */
  let notesCache = []

  const authToken = () => {
    try {
      const raw = localStorage.getItem('auth')
      return raw ? JSON.parse(raw).token : null
    } catch {
      return null
    }
  }

  async function loadNotes() {
    const token = authToken()
    if (!token) throw new Error('No auth token')
    const res = await fetch(
      `${apiRoot}/api/impowerX/unit-contracts/notes?unitContractId=${encodeURIComponent(unitContractId)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
    if (!res.ok) throw new Error(`load failed (${res.status})`)
    const data = await res.json()
    return Array.isArray(data.notes) ? data.notes : []
  }

  function renderNotesList(notes) {
    const list = Array.isArray(notes) ? notes : []
    notesCache = list.slice()
    listEl.querySelectorAll('.lux-uc-note-item').forEach((el) => el.remove())
    loadingEl.style.display = 'none'
    if (!list.length) {
      emptyEl.style.display = 'block'
      emptyEl.textContent = 'Keine Notizen vorhanden.'
      return
    }
    emptyEl.style.display = 'none'
    list.forEach((n) => {
      try {
        listEl.appendChild(createNoteElement(n))
      } catch (err) {
        console.error('[lux] unit contract note render failed', err, n)
      }
    })
    if (!listEl.querySelector('.lux-uc-note-item')) {
      emptyEl.style.display = 'block'
      emptyEl.textContent = 'Notizen konnten nicht angezeigt werden.'
    }
  }

  addBtn.addEventListener('click', async () => {
    const text = textarea.value.trim()
    if (!text) return
    const token = authToken()
    if (!token) {
      alert('Bitte erneut anmelden.')
      return
    }
    const mergeSavedNoteIntoCache = (savedNote, cache) => {
      const id = String(savedNote.id)
      const idx = cache.findIndex((n) => String(n.id) === id)
      const next =
        idx >= 0 ? cache.map((n, i) => (i === idx ? savedNote : n)) : [savedNote, ...cache]
      return next.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    }

    const body = {
      unitContractId,
      note: text,
      editsNoteId: editsNoteId || undefined,
    }
    let res
    try {
      res = await fetch(`${apiRoot}/api/impowerX/unit-contracts/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      })
    } catch (e) {
      console.error(e)
      alert('Notiz konnte nicht gespeichert werden.')
      return
    }
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      console.error(data.error || res.status)
      alert('Notiz konnte nicht gespeichert werden.')
      return
    }

    const savedNote = data.note
    let notes
    let reloadFailed = false
    try {
      notes = await loadNotes()
    } catch (e) {
      console.error(e)
      reloadFailed = true
      if (savedNote) {
        notes = mergeSavedNoteIntoCache(savedNote, notesCache)
      } else {
        alert(
          'Notiz wurde vermutlich gespeichert, die Liste konnte aber nicht aktualisiert werden. Bitte Seite neu laden.'
        )
        return
      }
    }

    renderNotesList(notes)
    textarea.value = ''
    editsNoteId = null
    addBtn.querySelector('span').textContent = 'Erstellen'
    if (reloadFailed) {
      alert(
        'Notiz gespeichert. Die vollständige Liste konnte nicht neu geladen werden — bei Bedarf Seite neu laden.'
      )
    }
  })

  listEl.addEventListener('click', async (event) => {
    const delBtn = event.target.closest('.lux-uc-delete-note-btn')
    if (delBtn) {
      const item = delBtn.closest('.lux-uc-note-item')
      const noteId = item && item.dataset.noteId
      if (!noteId || !confirm('Diese Notiz wirklich löschen?')) return
      const token = authToken()
      if (!token) return
      try {
        const res = await fetch(
          `${apiRoot}/api/impowerX/unit-contracts/notes?noteId=${encodeURIComponent(noteId)}`,
          { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
        )
        if (!res.ok) throw new Error('delete failed')
        item.remove()
        if (!listEl.querySelector('.lux-uc-note-item')) {
          emptyEl.style.display = 'block'
          emptyEl.textContent = 'Keine Notizen vorhanden.'
        }
      } catch {
        alert('Löschen fehlgeschlagen.')
      }
      return
    }

    const editBtn = event.target.closest('.lux-uc-edit-note-btn')
    if (editBtn) {
      const item = editBtn.closest('.lux-uc-note-item')
      const bodyEl = item && item.querySelector('.lux-uc-note-body')
      if (!bodyEl) return
      textarea.value = (bodyEl.textContent || '').trim()
      editsNoteId = item.dataset.noteId
      item.remove()
      addBtn.querySelector('span').textContent = 'Speichern'
      if (!listEl.querySelector('.lux-uc-note-item')) {
        emptyEl.style.display = 'block'
        emptyEl.textContent = 'Keine Notizen vorhanden.'
      }
    }
  })

  let isOpen = false
  let didLoadNotes = false

  const closeSectionDom = (wc) => {
    wc.classList.remove('section-wrapper-open')
    const content = wc.querySelector(':scope > .section-content')
    const foot = wc.querySelector(':scope > .section-footer')
    if (content) content.style.display = 'none'
    if (foot) foot.style.display = 'none'
  }



  const applyOpenState = (open) => {
    isOpen = open
    if (open) {
      wrapperContent.classList.add('section-wrapper-open')
      sectionBody.style.display = ''
      if (!didLoadNotes) {
        didLoadNotes = true
        loadNotes()
          .then(renderNotesList)
          .catch((err) => {
            console.error('[lux] unit contract notes load failed', err)
            loadingEl.style.display = 'none'
            emptyEl.style.display = 'block'
            emptyEl.textContent =
              'Notizen konnten nicht geladen werden. Bitte versuche es später noch einmal.'
          })
      }
    } else {
      wrapperContent.classList.remove('section-wrapper-open')
      sectionBody.style.display = 'none'
    }
  }

  const toggle = () => applyOpenState(!isOpen)

  const onOtherSectionHeaderClick = (e) => {
    const headerEl = e.target.closest('.section-header')
    if (!headerEl) return
    const clickedWrapper = headerEl.closest('.section-wrapper-content')
    if (!clickedWrapper || clickedWrapper === wrapperContent) return
    if (isOpen) applyOpenState(false)
  }
  pageContent.addEventListener('click', onOtherSectionHeaderClick, true)

  header.addEventListener('click', (e) => {
    e.preventDefault()
    toggle()
  })
  header.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      toggle()
    }
  })

  wrapperContent.appendChild(header)
  wrapperContent.appendChild(sectionBody)
  root.appendChild(wrapperContent)
  pageContent.appendChild(root)
}
